import {openDB, type DBSchema, type IDBPDatabase} from 'idb';
import type {QueuedMutation} from '@/state/offlineQueue';

/**
 * IndexedDB-backed store for pending offline submissions.
 *
 * Chosen over localStorage: no realistic size ceiling (localStorage
 * caps around 5-10MB and is string-only), transactional writes, and
 * async access so a large queue never blocks the UI thread. This is
 * the browser-native equivalent of on-device SQLite for this
 * purpose — see README for the fuller rationale, including when a
 * literal SQLite (native wrapper, or sqlite-wasm+OPFS) would
 * actually be worth the extra complexity.
 *
 * Keyed by `entityKey` (e.g. "well:123") rather than a generated id,
 * so a second offline edit to the same record naturally overwrites
 * the first queued one instead of requiring separate dedupe logic.
 */

const DB_NAME = 'well-data-app';
// Bumped from 1 -> 2 to add PHOTO_STORE_NAME below (IndexedDB object
// stores can only be created inside a version-change transaction, i.e.
// idb's `upgrade` callback — a plain reopen at the same version won't
// pick up a new store).
const DB_VERSION = 2;
const STORE_NAME = 'pending-mutations';
// Run ticket photos captured while offline, or while online but not yet
// uploaded (see components/ScanTicketButton.tsx / pages/RunTicketFormPanel.tsx)
// — kept in a separate store from PENDING mutations themselves because a
// Blob doesn't belong mixed into a QueuedMutation's JSON-ish variables,
// and because this store's entries get cleaned up on a different
// trigger than the mutation queue's own entries: NOT when the ticket
// record's own create/update mutation succeeds (that used to be the
// trigger — see the REMOVED note on offlineQueue.ts's applySyncResultAtom,
// fixed 2026-08-20), but only once this specific photo has actually been
// uploaded to S3 AND attached via its own follow-up mutation (see
// state/runTicketPhotoSync.ts). Those are two genuinely separate
// successes — a ticket record can sync fine while its photo upload is
// still sitting in a spotty-signal retry loop, and deleting the local
// blob on the wrong one of those was exactly the bug that let a photo
// silently vanish after a reload (docs/run-ticket-scan-spec.md).
// Deliberately NOT meant to hold every photo ever taken — only ones
// still waiting to sync. See the comment on PendingPhoto below.
const PHOTO_STORE_NAME = 'pending-photos';

export interface PendingPhoto {
   entityKey: string;
   /** The RunTicket this photo belongs to — the attach mutation's target
    * id. Distinct from entityKey (which is shaped for the mutation
    * queue's own dedupe convention, e.g. `run-ticket:new:${id}`) so the
    * sync step never has to parse an id back out of a string it wasn't
    * designed to be parsed from. */
   runTicketId: string;
   blob: Blob;
   contentType: string;
   /** Date.now() when this was queued (set by putPendingPhoto, below) —
    * lets state/runTicketPhotoSync.ts tell a photo that's still
    * genuinely waiting on its RunTicket to be created (a save just in
    * flight, or this running before the RunTicket list has loaded once
    * at app start) apart from one that never will be (e.g. a scan on a
    * new-ticket draft that was closed/refreshed away without ever
    * saving — see ScanTicketButton.tsx's own doc comment on why that
    * case has no reliable in-app cleanup to fall back on) and clean the
    * latter up instead of retrying it forever, silently, on every
    * reload. Optional only because entries written before this field
    * existed won't have it — treated as "unknown, assume old" by the
    * code that reads it, which is exactly the right call for those
    * (already-orphaned, pre-existing) entries. */
   capturedAt?: number;
}

interface OfflineDBSchema extends DBSchema {
   [STORE_NAME]: {
      key: string;
      value: QueuedMutation;
   };
   [PHOTO_STORE_NAME]: {
      key: string;
      value: PendingPhoto;
   };
}

let dbPromise: Promise<IDBPDatabase<OfflineDBSchema>> | null = null;

function getDb() {
   if (!dbPromise) {
      const openStartedAt = Date.now();
      // ADDED 2026-10-02 (Duane: saving a run ticket with a new photo sat on
      // "Saving…" and the photo sync never even logged its first line): an
      // IndexedDB open that is BLOCKED — e.g. another tab of this app still
      // holding an older-version connection open while this one needs to
      // upgrade — never resolves or rejects, so every awaiting caller
      // (queue hydration, photo capture, photo sync) just hangs silently.
      // These handlers make that visible and let a connection step aside:
      // blocking() closes THIS tab's connection when another tab is asking
      // to upgrade, and terminated() forces a fresh open next time.
      const slowOpenTimer = setTimeout(() => {
         console.warn(
            `[offlineDb] opening the local database has taken ${Date.now() - openStartedAt}ms — ` +
               'another open tab of this app may be blocking it; close other tabs and reload',
         );
      }, 3000);
      // Shared by the normal open and the newer-version fallback below, so
      // EITHER connection steps aside when another tab needs to upgrade/delete.
      const connectionHandlers = {
         blocked() {
            console.warn('[offlineDb] upgrade blocked by another open tab/connection of this app — close it');
         },
         blocking() {
            console.warn('[offlineDb] another tab needs to upgrade the local database — closing this connection');
            void dbPromise?.then((db) => db.close()).catch(() => {});
            dbPromise = null;
         },
         terminated() {
            console.warn('[offlineDb] local database connection was terminated by the browser — will reopen');
            dbPromise = null;
         },
      };
      dbPromise = openDB<OfflineDBSchema>(DB_NAME, DB_VERSION, {
         upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
               db.createObjectStore(STORE_NAME, {keyPath: 'entityKey'});
            }
            if (!db.objectStoreNames.contains(PHOTO_STORE_NAME)) {
               db.createObjectStore(PHOTO_STORE_NAME, {keyPath: 'entityKey'});
            }
         },
         ...connectionHandlers,
      });
      // VersionError = the browser already holds a NEWER schema version than
      // DB_VERSION (typically a different build/branch served from this same
      // origin, e.g. localhost:5173, bumped it). Every call used to fail with
      // it — silently breaking the offline queue and photo sync (a saved run
      // ticket's photo never uploaded). The stores this app uses are the same
      // in the newer version, so open the existing database as-is rather than
      // insisting on ours.
      dbPromise = dbPromise.catch((err) => {
         if (err instanceof DOMException && err.name === 'VersionError') {
            console.warn(
               `[offlineDb] local database is a newer version than this build expects (${DB_VERSION}) — opening it as-is`,
            );
            return openDB<OfflineDBSchema>(DB_NAME, undefined, connectionHandlers);
         }
         throw err;
      });
      dbPromise
         .then(() => clearTimeout(slowOpenTimer))
         .catch((err) => {
            clearTimeout(slowOpenTimer);
            console.error('[offlineDb] opening the local database failed', err);
            dbPromise = null;
         });
   }
   return dbPromise;
}

export async function getAllQueuedMutations(): Promise<QueuedMutation[]> {
   const db = await getDb();
   return db.getAll(STORE_NAME);
}

/** Insert or overwrite the queued entry for this entityKey. */
export async function putQueuedMutation(entry: QueuedMutation): Promise<void> {
   const db = await getDb();
   await db.put(STORE_NAME, entry);
}

export async function deleteQueuedMutation(entityKey: string): Promise<void> {
   const db = await getDb();
   await db.delete(STORE_NAME, entityKey);
}

/**
 * Persists a just-captured run ticket photo, keyed by the same
 * entityKey as its queued RUN_TICKET_CREATE_UPDATE mutation — so it
 * survives a reload while offline (unlike a plain
 * `URL.createObjectURL`, which only lives as long as the page does)
 * and can be picked back up once connectivity returns.
 */
export async function putPendingPhoto(
   entityKey: string,
   runTicketId: string,
   blob: Blob,
   contentType: string,
): Promise<void> {
   const db = await getDb();
   // capturedAt is always "now" — including on a retake overwriting the
   // same entityKey (see ScanTicketButton.tsx's handleFileChange), which
   // is correct: a fresh capture deserves a fresh abandonment clock, not
   // the original one it's replacing. See PendingPhoto's own doc comment.
   await db.put(PHOTO_STORE_NAME, {entityKey, runTicketId, blob, contentType, capturedAt: Date.now()});
}

export async function getPendingPhoto(entityKey: string): Promise<PendingPhoto | null> {
   const db = await getDb();
   const entry = await db.get(PHOTO_STORE_NAME, entityKey);
   return entry ?? null;
}

/** The whole not-yet-synced backlog — see state/runTicketPhotoSync.ts's syncAllPendingRunTicketPhotosAtom, the sole caller. */
export async function getAllPendingPhotos(): Promise<PendingPhoto[]> {
   const db = await getDb();
   return db.getAll(PHOTO_STORE_NAME);
}

/**
 * Called once THIS photo has actually been uploaded to S3 and attached
 * to its RunTicket (see state/runTicketPhotoSync.ts) — deliberately not
 * kept around after that, and deliberately NOT tied to the ticket
 * record's own mutation succeeding (see the comment above
 * PHOTO_STORE_NAME — that used to be the trigger, and was a bug). This
 * store is only ever meant to hold "not yet synced" photos, never a
 * growing archive of every photo ever taken; once a ticket is on the
 * backend, its photo is fetched over the network like any other image,
 * not kept in this local queue-only store. See
 * docs/run-ticket-scan-spec.md for why we don't try to guarantee
 * offline access to already-synced historical photos.
 */
export async function deletePendingPhoto(entityKey: string): Promise<void> {
   const db = await getDb();
   await db.delete(PHOTO_STORE_NAME, entityKey);
}
